import PropTypes from "prop-types";
import React, { useState } from "react";
import { convertFromRaw } from "draft-js";
import _ from "underscore";
import { outcomePropType } from "../../lib/dataShapes";
import { Qualities, outcomeWithData } from "../../lib/utils";
import {
  comparisonAbsCount,
  dichSofControl,
  interventionAbsCount,
  riskDifference
} from "../../lib/formatters";

import Collapsible from "../common/Collapsible";
import HeaderContentBox from "../common/HeaderContentBox";
import OutcomeEffectBox from "./OutcomeEffectBox";
import OutcomeName from "./OutcomeName";
import OutcomeScale from "./OutcomeScale";

import styles from "../../styles/outcomeTable.module.scss";
import RiskButton from "./RiskButton";

const risks = ["calculated", "low", "moderate", "high"];

const risksToBeDisplayed = outcome => {
  return _.chain(risks)
    .map(r => {
      const acc = {};
      acc["label"] =
        r === "calculated" ? "Study population" : outcome[`${r}ControlLabel`];

      acc["displayControl"] = outcome[`${r}Control`];
      acc["key"] = r;
      return acc;
    })
    .filter(r => r["displayControl"])
    .value();
};

const DichOutcomeTableRow = ({ intervention, comparison, outcome }) => {
  const outcomeHasData = outcomeWithData(outcome.metaType);
  const risks = risksToBeDisplayed(outcome);
  const [currentRisk, toggleRisk] = useState(_.first(risks).key);
  const hasComment = convertFromRaw(outcome.comments).hasText();

  return (
    <div className={styles.outcomeTableRow}>
      <Collapsible
        calculateHeight={true}
        expandable={outcomeHasData || hasComment}
      >
        <div className={styles.outcomeRow}>
          <div className={styles.outcomeNameCell}>
            <OutcomeName outcome={outcome} />
            {(risks.length > 1 || risks[0].key !== "calculated") && (
              <div className={styles.risksSwitcher}>
                <div>Baseline risk</div>
                <div>
                  {_.map(risks, r => (
                    <RiskButton
                      active={currentRisk === r["key"]}
                      key={r["key"]}
                      label={r["label"]}
                      onClick={e => {
                        e.stopPropagation();
                        toggleRisk(r["key"]);
                      }}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
          <div className={styles.effects}>
            <div className={styles.comparisonEffect}>
              <OutcomeEffectBox
                data={
                  outcomeHasData ? dichSofControl(outcome, currentRisk) : "-"
                }
                type={
                  outcome[`${currentRisk}ControlFavours`] === "comparison"
                    ? "violet"
                    : "white"
                }
              />
            </div>
            <div className={styles.interventionEffect}>
              <OutcomeEffectBox
                data={
                  outcomeHasData ? riskDifference(outcome, currentRisk) : "-"
                }
                type={
                  outcome[`${currentRisk}ControlFavours`] === "intervention"
                    ? "blue"
                    : "white"
                }
              />
            </div>
          </div>
          <div
            className={styles.outcomeCertainty}
            dangerouslySetInnerHTML={{
              __html:
                outcome.quality && `${Qualities[outcome.quality]}<br>certainty`
            }}
          />
        </div>
        <div className={styles.outcomeContent}>
          {outcomeHasData && (
            <OutcomeScale
              intervention={intervention}
              comparison={comparison}
              outcome={outcome}
              comparisonAbsCount={comparisonAbsCount(outcome, currentRisk)}
              interventionAbsCount={interventionAbsCount(outcome, currentRisk)}
              absDenominator={outcome.absDenominator}
              stepBy={outcome.absDenominator / 10}
              scaleTo={outcome.absDenominator}
            />
          )}
          {hasComment && (
            <div className={styles.whatHappens}>
              <HeaderContentBox
                title="What happens"
                content={outcome.comments}
              />
            </div>
          )}
        </div>
      </Collapsible>
    </div>
  );
};

DichOutcomeTableRow.propTypes = {
  comparison: PropTypes.string.isRequired,
  intervention: PropTypes.string.isRequired,
  outcome: PropTypes.shape(outcomePropType).isRequired
};

export default DichOutcomeTableRow;
